import axios from 'axios'

// For subdomain deployment, all API calls go through /api/
export const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds for AI responses
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('halcode-auth')
    if (token) {
      try {
        const { accessToken } = JSON.parse(token)
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`
        }
      } catch (e) {
        // Invalid token format
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const token = localStorage.getItem('halcode-auth')
        if (token) {
          const { refreshToken } = JSON.parse(token)
          const response = await axios.post('/api/auth/refresh', { refreshToken })
          const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data

          localStorage.setItem('halcode-auth', JSON.stringify({
            accessToken,
            refreshToken: newRefreshToken,
          }))

          originalRequest.headers.Authorization = `Bearer ${accessToken}`      
          return api(originalRequest)
        }
      } catch (e) {
        // Refresh failed, logout
        localStorage.removeItem('halcode-auth')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Streaming helper function
export async function* makeStreamingRequest(
  data: Record<string, unknown>,
  onChunk: (chunk: string) => void
): AsyncGenerator<string> {
  const token = localStorage.getItem('halcode-auth')
  let accessToken = ''
  if (token) {
    try {
      const { accessToken: at } = JSON.parse(token)
      accessToken = at
    } catch (e) {}
  }

  const response = await fetch('/api/chat/stream', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Response body is not readable')
  }

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6)
        if (data === '[DONE]') {
          return
        }
        try {
          const parsed = JSON.parse(data)
          if (parsed.content) {
            onChunk(parsed.content)
            yield parsed.content
          }
        } catch (e) {
          // Skip invalid JSON
        }
      }
    }
  }
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (email: string, password: string, name: string) =>
    api.post('/auth/register', { email, password, name }),

  logout: () => api.post('/auth/logout'),

  me: () => api.get('/auth/me'),

  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),       
}

// Chat API
export const chatApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get('/chat', { params }),

  get: (id: string) => api.get(`/chat/${id}`),

  create: (data: { title?: string; workflow?: string; model?: string }) =>    
    api.post('/chat', data),

  update: (id: string, data: { title?: string; workflow?: string; model?: string }) =>
    api.patch(`/chat/${id}`, data),

  delete: (id: string) => api.delete(`/chat/${id}`),

  sendMessage: (data: {
    content: string
    conversationId?: string
    workflow?: string
    model?: string
    attachments?: Array<{ name: string; type: string; content: string }>       
  }) => api.post('/chat/message', data),

  clearAll: () => api.delete('/chat'),
}

// Conversation helpers for chat store
export const conversations = {
  getConversations: async (userId: string) => {
    const response = await api.get<{ data: any[] }>('/chat', {            
      params: { userId }
    })
    return response.data
  },

  createConversation: async (data: { userId: string; title: string; mode: string }) => {
    const response = await api.post<{ data: any }>('/chat', data)      
    return response.data
  },

  deleteConversation: async (id: string) => {
    await api.delete(`/chat/${id}`)
  },

  getConversation: async (id: string) => {
    const response = await api.get<{ data: any }>(`/chat/${id}`)      
    return response.data
  },
}

// Codebase API
export const codebasesApi = {
  list: () => api.get('/codebases'),

  get: (id: string) => api.get(`/codebases/${id}`),

  create: (data: { name: string; description?: string; repoUrl?: string }) =>  
    api.post('/codebases', data),

  update: (id: string, data: { name?: string; description?: string }) =>       
    api.patch(`/codebases/${id}`, data),

  delete: (id: string) => api.delete(`/codebases/${id}`),

  search: (id: string, query: string, limit?: number) =>
    api.post(`/codebases/${id}/search`, { query, limit }),
}

// AI API
export const aiApi = {
  chat: (data: {
    messages: Array<{ role: string; content: string }>
    model: string
    provider: string
    maxTokens?: number
    temperature?: number
  }) => api.post('/ai/chat', data),

  chatStream: (data: {
    messages: Array<{ role: string; content: string }>
    model: string
    provider: string
    maxTokens?: number
    temperature?: number
  }) => api.post('/ai/chat/stream', data, { responseType: 'text' }),

  getModels: () => api.get('/ai/models'),

  setApiKey: (provider: string, apiKey: string) =>
    api.post('/ai/keys', { provider, apiKey }),

  deleteApiKey: (provider: string) =>
    api.delete(`/ai/keys/${provider}`),
}

// Subscription API
export const subscriptionApi = {
  getCurrent: () => api.get('/subscription'),

  createCheckout: (tier: 'pro' | 'team', priceId?: string) =>
    api.post('/subscription/checkout', { tier, priceId }),

  cancel: () => api.post('/subscription/cancel'),

  portal: () => api.post('/subscription/portal'),
}

// Admin API
export const adminApi = {
  getStats: () => api.get('/admin/stats'),

  listUsers: (params?: { page?: number; limit?: number; search?: string; tier?: string; role?: string }) =>
    api.get('/admin/users', { params }),

  updateUser: (id: string, data: { role?: string; subscriptionTier?: string }) =>
    api.patch(`/admin/users/${id}`, data),

  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),

  getAnalytics: (period?: string) =>
    api.get('/admin/analytics', { params: { period } }),

  getSubscriptions: () => api.get('/admin/subscriptions'),

  getSettings: () => api.get('/admin/settings'),
}
